import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import BusinessPartnerFunctionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-function/business-partner-function.entity";
import { ICreateBusinessPartnerFunction } from "./interfaces/create-business-partner-function.interface";
import { IUpdateBusinessPartnerFunction } from "./interfaces/update-business-partner-function.interface";
const apiUrl = env.API_BUSINESS_PARTNER;
const moduleName = "business-partner-function";

@Injectable({
  providedIn: "root",
})
export class BusinessPartnerFunctionService extends AbstractService<
  BusinessPartnerFunctionAttributes,
  ICreateBusinessPartnerFunction,
  IUpdateBusinessPartnerFunction
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene funciones de interlocutor con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param status Status filter
   * @returns Paginated data
   *
   * Search fields: code, name
   * Array filters: status
   */
  findAllWithCount(
    options: IPagination<BusinessPartnerFunctionAttributes>,
    status?
  ): Observable<IPaginationResult<BusinessPartnerFunctionAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerFunctionAttributes>[] => {
      const deletedFilter: FilterFn<BusinessPartnerFunctionAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<BusinessPartnerFunctionAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<BusinessPartnerFunctionAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BusinessPartnerFunctionAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<BusinessPartnerFunctionAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<BusinessPartnerFunctionAttributes[]>> {
    const query = buildQuery<BusinessPartnerFunctionAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["code","externalKeys", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerFunctionAttributes[]>>(API_URL);
  }

  /**
   * Searches active business partner functions for autocomplete.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code' y 'name'
   *
   * @param searchTerm - Search term for code or name (optional)
   * @param pageSize - Number of results to return (default: 10)
   * @returns Observable with filtered functions
   */
  searchActiveFunctions(
    searchTerm?: string,
    pageSize: number = 10
  ): Observable<IRequestResult<BusinessPartnerFunctionAttributes[]>> {
    const hasSearch = searchTerm && searchTerm.trim();

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, statusId=ACTIVE.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerFunctionAttributes>[] => {
      const deletedFilter: FilterFn<BusinessPartnerFunctionAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        andWhere('statusId', Ops.eq(StatusEnum.ACTIVE))
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<BusinessPartnerFunctionAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(searchTerm.trim(), "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<BusinessPartnerFunctionAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(searchTerm.trim(), "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BusinessPartnerFunctionAttributes>(
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      // Seleccionar solo los campos necesarios
      select(["code", "externalKeys", "name", "_id"]),
      // Ordenar por código
      orderBy("code", OrderVariant.Asc),
      // Paginación
      page(1, pageSize)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerFunctionAttributes[]>>(API_URL);
  }
}
